import React, { useState } from 'react';
import {
  Alert,
  Box,
  Button,
  CircularProgress,
  Container,
  CssBaseline,
  FormControl,
  Grid,
  InputLabel,
  MenuItem,
  Select,
  SelectChangeEvent,
  TextField,
  Typography,
} from '@mui/material';
import { useNavigate } from 'react-router-dom';
import { auth } from '../../middleware/auth0.service';
import {
  createStripeCustomer,
  RegisterBarber,
} from '../../middleware/registration_service/createRequest';
import {
  addBusinessHoursToBarber,
  updateBarberLocation,
  updateBarberUserId,
} from '../../middleware/registration_service/updateRequest';
import { getGeoCode } from '../../middleware/registration_service/getRequests';
import { parseGeocodeResponse } from '../../utils/geocodingUtils';
import { UK_CITIES } from '../../defaults/Cities';
import { Barber } from '../../types/barber';
import { hours } from '../../defaults/BusinessHours';
import { StripeCustomer } from '../../types/stripePayments';
import headerLogo from '../../images/header-logo.png';
import RegistrationCardDetails from './RegistrationCardDetails';
import {
  darkPageBg,
  darkFormContainerSx,
  darkTextFieldSx,
  darkSelectSx,
  darkSelectMenuProps,
  darkButtonSx,
} from '../components/darkFormStyles';

const TITLE_OPTIONS = ['Mr', 'Mrs', 'Miss', 'Dr', 'Prof'];

const BarberRegistration = () => {
  const navigate = useNavigate();
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState<string | null>(null);
  /** Shown after Auth0 user creation/profile save, before subscription selection. */
  const [showCardStep, setShowCardStep] = useState(false);
  const [registeredCustomerId, setRegisteredCustomerId] = useState<
    string | null
  >(null);

  const [form, setForm] = useState({
    title: '',
    firstName: '',
    lastName: '',
    email: '',
    phoneNumber: '',
    addressLineOne: '',
    addressLineTwo: '',
    city: '',
    country: 'United Kingdom',
    postcode: '',
    password: '',
    confirmPassword: '',
  });

  const handleChange = (e: React.ChangeEvent<HTMLInputElement>) => {
    setForm({ ...form, [e.target.name]: e.target.value });
  };

  const handleTitleChange = (e: SelectChangeEvent) => {
    setForm({ ...form, title: e.target.value });
  };

  const handleCityChange = (e: SelectChangeEvent) => {
    setForm({ ...form, city: e.target.value });
  };

  const handleSubmit = async (e: React.FormEvent<HTMLFormElement>) => {
    e.preventDefault();
    setError(null);
    let customerId: string | undefined;

    if (form.password !== form.confirmPassword) {
      setError('Passwords do not match.');
      return;
    }

    if (
      !form.title ||
      !form.firstName ||
      !form.lastName ||
      !form.email ||
      !form.addressLineOne ||
      !form.city ||
      !form.postcode ||
      !form.password
    ) {
      setError('Please fill in all required fields.');
      return;
    }

    setLoading(true);
    try {
      // 1. Create Stripe customer
      try {
        const response = await createStripeCustomer({
          email: form.email,
          name: `${form.firstName} ${form.lastName}`,
        });
        const stripeCustomer: StripeCustomer = response.data;
        customerId = stripeCustomer.id;
        localStorage.setItem('reg_customer_id', stripeCustomer.id);
      } catch (stripeErr) {
        console.warn('Could not create Stripe customer:', stripeErr);
        setLoading(false);
        setError('Failed to create Stripe customer. Please try again.');
        return;
      }

      // 2. Register Barber in backend
      const barberData: Barber = {
        customerId: customerId,
        title: form.title,
        firstName: form.firstName,
        lastName: form.lastName,
        email: form.email,
        phoneNumber: form.phoneNumber,
        addressLineOne: form.addressLineOne,
        addressLineTwo: form.addressLineTwo,
        city: form.city,
        country: form.country,
        postcode: form.postcode,
        subscriptionStatus: 'Active',
        mobile: true,
      };
      const barberRes = await RegisterBarber(barberData);
      const barberId: bigint = barberRes.data.id;

      if (barberId) {
        await addBusinessHoursToBarber(hours, barberId);
      }
      localStorage.setItem('reg_partner_id', barberId.toString());

      // Geocode the barber's address and persist coordinates (best-effort;
      // a geocoding failure should not block registration).
      try {
        const address = [
          form.addressLineOne,
          form.addressLineTwo,
          form.city,
          form.postcode,
          form.country,
        ]
          .filter(Boolean)
          .join(', ');
        const geocodeRes = await getGeoCode(address);
        const coordinates = parseGeocodeResponse(geocodeRes.data);
        if (coordinates) {
          await updateBarberLocation(
            barberId,
            coordinates.lat,
            coordinates.lng
          );
        } else {
          console.warn('Could not resolve coordinates for barber address.');
        }
      } catch (geoErr) {
        console.error('Failed to geocode/save barber location:', geoErr);
      }

      // 3. Register in Auth0
      auth.signup(
        {
          email: form.email,
          password: form.password,
          connection: 'Username-Password-Authentication',
          userMetadata: {
            firstName: form.firstName,
            lastName: form.lastName,
          },
        },
        async (err: any, result: any) => {
          if (err) {
            setError(
              err.code === 'invalid_signup'
                ? 'This email already exists. Please log in.'
                : err.code === 'invalid_password'
                ? 'Password is not strong enough.'
                : err.description || err.code || 'Registration failed.'
            );
            setLoading(false);
            setError('Failed to register in Auth0. Please try again.');
            return;
          }

          const auth0UserId = result.Id || result._id || result.id || '';
          localStorage.setItem('reg_auth0_user_id', String(auth0UserId));

          // 4. Update Barber with Auth0 User Id (best-effort)
          try {
            await updateBarberUserId(barberId, String(auth0UserId));
          } catch (updateErr) {
            setError(
              'Failed to update barber with Auth0 User ID. Please try again.'
            );
          }

          setLoading(false);
          // Auth0 save completed — show the mandatory card step before
          // allowing navigation to subscription selection.
          setRegisteredCustomerId(customerId ?? null);
          setShowCardStep(true);
        }
      );
    } catch (err: any) {
      setError(
        err?.response?.data?.message ||
          'An error occurred during registration. Please try again.'
      );
      setLoading(false);
    }
  };

  if (showCardStep && registeredCustomerId) {
    return (
      <RegistrationCardDetails
        customerId={registeredCustomerId}
        onSuccess={() => navigate('/register/subscription')}
      />
    );
  }

  return (
    <Box sx={{ bgcolor: darkPageBg, minHeight: '100vh', py: 8 }}>
      <Container
        component="main"
        maxWidth="sm"
        sx={{ ...darkFormContainerSx, mt: 4, mb: 4, p: 4 }}
      >
        <CssBaseline />
        <Box
          sx={{
            display: 'flex',
            flexDirection: 'column',
            alignItems: 'center',
          }}
        >
          <Box
            component="img"
            src={headerLogo}
            alt="Barbers Limited"
            sx={{ width: 88, height: 88, objectFit: 'contain', mb: 1 }}
          />
          <Typography
            component="h1"
            variant="h5"
            fontWeight="bold"
            sx={{ color: '#fff' }}
          >
            Independent Barber Registration
          </Typography>
          <Typography
            variant="body2"
            sx={{ mt: 1, textAlign: 'center', color: 'rgba(255,255,255,0.65)' }}
          >
            Register as an independent / mobile barber
          </Typography>

          {error && (
            <Alert severity="error" sx={{ mt: 2, width: '100%' }}>
              {error}
            </Alert>
          )}

          <Box
            component="form"
            noValidate
            onSubmit={handleSubmit}
            sx={{ mt: 3, width: '100%' }}
          >
            <Grid container spacing={2}>
              {/* Personal Details */}
              <Grid item xs={12}>
                <Typography
                  variant="subtitle1"
                  fontWeight="bold"
                  sx={{ color: 'rgba(255,255,255,0.7)' }}
                >
                  Personal Details
                </Typography>
              </Grid>
              <Grid item xs={12}>
                <FormControl fullWidth required sx={darkSelectSx}>
                  <InputLabel id="title-label">Title</InputLabel>
                  <Select
                    labelId="title-label"
                    value={form.title}
                    label="Title"
                    onChange={handleTitleChange}
                    MenuProps={darkSelectMenuProps}
                  >
                    {TITLE_OPTIONS.map((t) => (
                      <MenuItem key={t} value={t}>
                        {t}
                      </MenuItem>
                    ))}
                  </Select>
                </FormControl>
              </Grid>
              <Grid item xs={12} sm={6}>
                <TextField
                  required
                  fullWidth
                  label="First Name"
                  name="firstName"
                  value={form.firstName}
                  onChange={handleChange}
                  autoFocus
                  sx={darkTextFieldSx}
                />
              </Grid>
              <Grid item xs={12} sm={6}>
                <TextField
                  required
                  fullWidth
                  label="Last Name"
                  name="lastName"
                  value={form.lastName}
                  onChange={handleChange}
                  sx={darkTextFieldSx}
                />
              </Grid>
              <Grid item xs={12}>
                <TextField
                  required
                  fullWidth
                  label="Email Address"
                  name="email"
                  type="email"
                  value={form.email}
                  onChange={handleChange}
                  sx={darkTextFieldSx}
                />
              </Grid>
              <Grid item xs={12}>
                <TextField
                  fullWidth
                  label="Phone Number"
                  name="phoneNumber"
                  value={form.phoneNumber}
                  onChange={handleChange}
                  sx={darkTextFieldSx}
                />
              </Grid>

              {/* Address */}
              <Grid item xs={12} sx={{ mt: 1 }}>
                <Typography
                  variant="subtitle1"
                  fontWeight="bold"
                  sx={{ color: 'rgba(255,255,255,0.7)' }}
                >
                  Address
                </Typography>
              </Grid>
              <Grid item xs={12}>
                <TextField
                  required
                  fullWidth
                  label="Address Line 1"
                  name="addressLineOne"
                  value={form.addressLineOne}
                  onChange={handleChange}
                  sx={darkTextFieldSx}
                />
              </Grid>
              <Grid item xs={12}>
                <TextField
                  fullWidth
                  label="Address Line 2"
                  name="addressLineTwo"
                  value={form.addressLineTwo}
                  onChange={handleChange}
                  sx={darkTextFieldSx}
                />
              </Grid>
              <Grid item xs={12} sm={6}>
                <FormControl fullWidth required sx={darkSelectSx}>
                  <InputLabel id="city-label">City</InputLabel>
                  <Select
                    labelId="city-label"
                    value={form.city}
                    label="City"
                    onChange={handleCityChange}
                    MenuProps={darkSelectMenuProps}
                  >
                    {UK_CITIES.map((c) => (
                      <MenuItem key={c} value={c}>
                        {c}
                      </MenuItem>
                    ))}
                  </Select>
                </FormControl>
              </Grid>
              <Grid item xs={12} sm={6}>
                <TextField
                  fullWidth
                  label="Country"
                  name="country"
                  value={form.country}
                  onChange={handleChange}
                  InputProps={{ readOnly: true }}
                  sx={darkTextFieldSx}
                />
              </Grid>
              <Grid item xs={12}>
                <TextField
                  required
                  fullWidth
                  label="Post Code"
                  name="postcode"
                  value={form.postcode}
                  onChange={handleChange}
                  sx={darkTextFieldSx}
                />
              </Grid>

              {/* Security */}
              <Grid item xs={12} sx={{ mt: 1 }}>
                <Typography
                  variant="subtitle1"
                  fontWeight="bold"
                  sx={{ color: 'rgba(255,255,255,0.7)' }}
                >
                  Account Security
                </Typography>
              </Grid>
              <Grid item xs={12}>
                <TextField
                  required
                  fullWidth
                  label="Password"
                  name="password"
                  type="password"
                  value={form.password}
                  onChange={handleChange}
                  autoComplete="new-password"
                  sx={darkTextFieldSx}
                />
              </Grid>
              <Grid item xs={12}>
                <TextField
                  required
                  fullWidth
                  label="Confirm Password"
                  name="confirmPassword"
                  type="password"
                  value={form.confirmPassword}
                  onChange={handleChange}
                  sx={darkTextFieldSx}
                />
              </Grid>
            </Grid>

            <Button
              type="submit"
              fullWidth
              variant="contained"
              disabled={loading}
              sx={{ mt: 3, mb: 2, py: 1.5, ...darkButtonSx }}
            >
              {loading ? (
                <CircularProgress size={24} color="inherit" />
              ) : (
                'Select Plan'
              )}
            </Button>
          </Box>
        </Box>
      </Container>
    </Box>
  );
};

export default BarberRegistration;
